import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { fetchMe, updateName } from './api';
import type { StaffMe, StaffState } from './types';

// Mission Control's boot fetch — replaces the analytics platform's
// ClientsProvider /profile call. 403 means "authenticated but not in the
// staff registry" and renders the Not-Configured screen, not an error.
export const fetchStaffMe = createAsyncThunk<StaffMe, void, { rejectValue: 'forbidden' | 'error' }>(
  'staff/fetchMe',
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMe();
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      return rejectWithValue(status === 403 ? 'forbidden' : 'error');
    }
  },
);

// Self-service profile edits live here rather than in the Settings controller:
// `me` is app-level identity, read by the sidebar as well as the Settings page.
export const saveName = createAsyncThunk<StaffMe, string>('staff/saveName', async (name) =>
  updateName(name),
);

const initialState: StaffState = {
  loaded: false,
  notConfigured: false,
  error: false,
  me: null,
};

const staffSlice = createSlice({
  name: 'staff',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffMe.pending, (state) => {
        state.error = false;
      })
      .addCase(fetchStaffMe.fulfilled, (state, action) => {
        state.loaded = true;
        state.notConfigured = false;
        state.me = action.payload;
      })
      .addCase(fetchStaffMe.rejected, (state, action) => {
        state.loaded = true;
        state.me = null;
        if (action.payload === 'forbidden') state.notConfigured = true;
        else state.error = true;
      })
      .addCase(saveName.fulfilled, (state, action) => {
        state.me = action.payload;
      });
  },
});

export const staffReducer = staffSlice.reducer;
